const state = {
  authTokens: {}, // { inboxId: { token: 'abc123', agentCode: 'code' } }
  isLoading: false,
};

const getters = {
  getAuthTokenByInboxId: moduleState => inboxId => {
    const tokenData = moduleState.authTokens[inboxId];
    if (!tokenData) return null;

    return tokenData.token;
  },

  isTokenValidForInbox: moduleState => (inboxId, agentCode) => {
    const tokenData = moduleState.authTokens[inboxId];
    if (tokenData && tokenData?.agentCode === agentCode) return true;
    return false;
  },

  getLoadingState: moduleState => moduleState.isLoading,
};

const mutations = {
  SET_AUTH_TOKEN(moduleState, { inboxId, token, agentCode }) {
    moduleState.authTokens = {
      ...moduleState.authTokens,
      [inboxId]: { token, agentCode },
    };

    // Also store in localStorage
    const tokenData = { token, agentCode };
    localStorage.setItem(`agentic_auth_${inboxId}`, JSON.stringify(tokenData));
  },

  REMOVE_AUTH_TOKEN(moduleState, inboxId) {
    const { [inboxId]: removed, ...rest } = moduleState.authTokens;
    moduleState.authTokens = rest;

    // Also remove from localStorage
    localStorage.removeItem(`agentic_auth_${inboxId}`);
  },

  SET_LOADING(moduleState, loading) {
    moduleState.isLoading = loading;
  },

  RESTORE_TOKENS_FROM_STORAGE(moduleState) {
    // Restore all tokens from localStorage on app init
    const tokenKeys = Object.keys(localStorage).filter(key =>
      key.startsWith('agentic_auth_')
    );

    tokenKeys.forEach(key => {
      const inboxId = key.replace('agentic_auth_', '');
      try {
        const tokenData = JSON.parse(localStorage.getItem(key));

        // Check if token is still valid
        if (tokenData) {
          moduleState.authTokens[inboxId] = tokenData;
        } else {
          // Remove expired token
          localStorage.removeItem(key);
        }
      } catch (error) {
        // Remove corrupted token data
        localStorage.removeItem(key);
      }
    });
  },
};

const actions = {
  async fetchAuthToken(
    { commit, getters: moduleGetters },
    { inboxId, agentCode }
  ) {
    // Check if we already have a valid token
    if (moduleGetters.isTokenValidForInbox(inboxId, agentCode)) {
      return moduleGetters.getAuthTokenByInboxId(inboxId);
    }

    commit('SET_LOADING', true);

    try {
      // Get the base URL from window.chatwootConfig
      const { quantAppApiBaseUrl = 'https://v2.a.rnd.quant.ai' } =
        window.chatwootConfig || {};
      const apiUrl = `${quantAppApiBaseUrl}/api/passport`;

      const response = await fetch(apiUrl, {
        headers: {
          'Content-Type': 'application/json',
          'x-app-code': agentCode,
        },
      });

      if (response.ok) {
        const data = await response.json();

        commit('SET_AUTH_TOKEN', {
          inboxId,
          token: data.access_token,
          agentCode,
        });

        return data.access_token;
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `HTTP ${response.status}: ${errorData.message || response.statusText}`
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to fetch auth token:', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async initializeAuth(
    { dispatch, getters: moduleGetters },
    { inboxId, agentCode }
  ) {
    await dispatch('restoreTokensFromStorage');

    if (!moduleGetters.isTokenValidForInbox(inboxId, agentCode)) {
      return dispatch('fetchAuthToken', { inboxId, agentCode });
    }
    return moduleGetters.getAuthTokenByInboxId(inboxId);
  },

  clearAuthToken({ commit }, inboxId) {
    commit('REMOVE_AUTH_TOKEN', inboxId);
  },

  clearAllAuthTokens({ commit, state: moduleState }) {
    Object.keys(moduleState.authTokens).forEach(inboxId => {
      commit('REMOVE_AUTH_TOKEN', inboxId);
    });
  },

  restoreTokensFromStorage({ commit }) {
    commit('RESTORE_TOKENS_FROM_STORAGE');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
